import {Button, Dialog, DotProgress, Link} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {type ReactNode} from 'react';

import {
  useRunOverviewViewCodeModalQuery,
  type ViewCodeModal_RunFragment,
} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import * as RunHelpers from '../../util/runhelpers';
import * as urls from '../../util/urls';

type ViewCodeModalChromeProps = {
  children: ReactNode;
  isOpen: boolean;
  onOpenChange(open: boolean): void;
};
function ViewCodeModalChrome({
  children,
  isOpen,
  onOpenChange,
}: ViewCodeModalChromeProps) {
  return (
    <Dialog.Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Heading>View Code</Dialog.Heading>
      <Dialog.Content>
        <TailwindContents>{children}</TailwindContents>
      </Dialog.Content>
    </Dialog.Modal>
  );
}

type ViewCodeModalBodyProps = {
  entityName: string;
  projectName: string;
  run: ViewCodeModal_RunFragment;
  runName: string;
};
function ViewCodeModalBody({
  entityName,
  projectName,
  run,
  runName,
}: ViewCodeModalBodyProps) {
  const hasGitHub = run.commit && run.github;

  return (
    <div className="flex flex-col gap-8">
      <p>
        Wandb will automatically save the git commit SHA of the last commit
        before each of your runs.
      </p>
      {hasGitHub ? (
        <p>
          You can find your commit{' '}
          <Link href={run.github ?? ''} target="_blank">
            here on GitHub
          </Link>
          .
        </p>
      ) : run.runInfo && run.runInfo.git ? (
        <table>
          <tbody>
            <tr>
              <th>Git remote</th>
              <td>
                <code className="text-[14px]">
                  {run.runInfo.git.remote ?? '-'}
                </code>
              </td>
            </tr>
            <tr>
              <th>Git commit</th>
              <td>
                <code className="text-[14px]">
                  {run.runInfo.git.commit ?? '-'}
                </code>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <p>However, we couldn't find a git commit for this particular run.</p>
      )}
      {RunHelpers.hasDiffPatchFile(run.viewCodeModalPatchFile) && (
        <p>
          Your uncommitted changes were saved in file called{' '}
          <strong>diff.patch</strong>, which you can download{' '}
          <Link
            href={urls.makeDownloadUrl(
              urls.runDiffPatchFileUrl({
                entityName,
                projectName,
                runName,
              }),
              'diff.patch'
            )}
            download="diff.patch">
            here
          </Link>
          .
        </p>
      )}
    </div>
  );
}

type ViewCodeModalProps = {
  entityName: string;
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  projectName: string;
  runName: string;
};
export function ViewCodeModal({
  entityName,
  projectName,
  runName,
  isOpen,
  onOpenChange,
}: ViewCodeModalProps) {
  const {data, loading, error, refetch} = useRunOverviewViewCodeModalQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
    skip: !isOpen,
    context: propagateErrorsContext(),
  });

  if (loading) {
    return (
      <ViewCodeModalChrome isOpen={isOpen} onOpenChange={onOpenChange}>
        <div className="py-32">
          <DotProgress aria-label="Loading run info" size="lg" />
        </div>
      </ViewCodeModalChrome>
    );
  }

  const run = data?.project?.run;

  if (error || !run) {
    return (
      <ViewCodeModalChrome isOpen={isOpen} onOpenChange={onOpenChange}>
        <div className="flex gap-16 flex-col">
          <p>Could not load run source code information!</p>
          <Button
            startIcon="reload-refresh"
            variant="secondary"
            onPress={() => refetch()}>
            Retry
          </Button>
        </div>
      </ViewCodeModalChrome>
    );
  }

  return (
    <ViewCodeModalChrome isOpen={isOpen} onOpenChange={onOpenChange}>
      <ViewCodeModalBody
        entityName={entityName}
        projectName={projectName}
        run={run}
        runName={runName}
      />
    </ViewCodeModalChrome>
  );
}
